import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import ContactService from '../../services/contact.service';

const ContactList: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await ContactService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
      });
      setContacts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await ContactService.delete(id);
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const getInitials = (firstName: string, lastName?: string) => {
    return `${firstName[0]}${lastName ? lastName[0] : ''}`.toUpperCase();
  };

  const getSourceColor = (source: string) => {
    const colors: any = {
      manual: 'default',
      import: 'info',
      whatsapp: 'success',
      instagram: 'secondary',
      website: 'primary',
      other: 'default',
    };
    return colors[source] || 'default';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Contacts</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/contact-groups')}
          >
            Manage Groups
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/contacts/create')}
          >
            New Contact
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Social</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar>
                        {getInitials(
                          contact.attributes?.firstName,
                          contact.attributes?.lastName
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {contact.attributes?.firstName} {contact.attributes?.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {contact.attributes?.position || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {contact.attributes?.phone}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {contact.attributes?.email ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{contact.attributes?.email}</Typography>
                      </Stack>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{contact.attributes?.company || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={contact.attributes?.source}
                      size="small"
                      color={getSourceColor(contact.attributes?.source)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {contact.attributes?.whatsappNumber && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() =>
                            navigate(`/operations/whatsapp-chat/${contact.id}`)
                          }
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      )}
                      {contact.attributes?.instagramHandle && (
                        <IconButton size="small" color="secondary">
                          <InstagramIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contact.attributes?.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={contact.attributes?.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/contacts/${contact.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(contact.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && contacts.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No contacts found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ContactList;

